import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpToLine,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ClipboardPaste,
  Copy,
  Download,
  Search,
  Sparkles,
  Star,
  Trash2,
  Upload,
  UserMinus,
} from 'lucide-react';
import ImportSignupsDialog from '@/components/ImportSignupsDialog';
import {
  EVENT_TYPE_LABEL,
  EVENT_TYPE_SHORT,
  api,
  canEdit,
  type AssignedRole,
  type AuthUser,
  type Candidate,
  type EventChangeLogEntry,
  type EventStatus,
  type EventSummary,
  type EventType,
  type ImportVideoJobState,
  type LeavePeriod,
  type Participation,
  type PowerSnapshot,
  type Rank,
  type SlotKey,
} from '@/lib/api';
import { cn, formatPower } from '@/lib/utils';
import { type AutofillMode, scoreBreakdown } from '@/lib/selectionScore';
import { useScorePolicy } from '@/lib/useScorePolicy';
import { type AutofillPlan, planDistributeEven, planSlotAutofill } from '@/lib/autofillPlan';
import AttendancePanel from '@/components/AttendancePanel';
import { buildSlotDmText, DM_TEXT_MAX } from '@/lib/slotDm';
import { PlayerLabel } from '@/components/PlayerLabel';
import { PlayerFlagChip } from '@/components/PlayerFlagChip';
import { RecentEventsChip } from '@/components/RecentEventsChip';
import { ScoreBadge } from '@/components/ScoreBadge';
import { AutofillPreviewModal } from '@/components/AutofillPreviewModal';
import SpecialEventDetail from './SpecialEventDetail';

function LeaveMarker({ leave }: { leave: LeavePeriod | null | undefined }) {
  if (!leave) return null;
  const range = leave.endsAt
    ? `${leave.startsAt} – ${leave.endsAt}`
    : `from ${leave.startsAt}`;
  const title = leave.reason ? `On leave (${range}) — ${leave.reason}` : `On leave (${range})`;
  return (
    <span
      title={title}
      className="inline-flex items-center rounded bg-teal-900/50 px-1 py-0.5 text-[10px] font-semibold text-teal-200"
    >
      🌴
    </span>
  );
}

const STATUS_STYLES: Record<EventStatus, string> = {
  planned: 'bg-indigo-700/50 text-indigo-100',
  completed: 'bg-emerald-700/50 text-emerald-100',
  skipped: 'bg-slate-700 text-slate-300',
};

function playerLabel(p: Participation) {
  return (p.player.displayName?.trim() || p.player.name).trim();
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const RANK_ORDER: Rank[] = ['R5', 'R4', 'R3', 'R2', 'R1'];

function thpOf(p: { player: { latestSnapshot: PowerSnapshot | null } }): number {
  return p.player.latestSnapshot?.totalPower ?? 0;
}

// Sort a list of items (participations or candidates) into R5→R1 groups,
// each group sorted by THP (total power) desc. Same order the game uses in
// its alliance / apply lists — hence "like in-game".
function groupByRankByThp<T extends { player: { rank: Rank; latestSnapshot: PowerSnapshot | null } }>(
  items: T[],
): { rank: Rank; rows: T[] }[] {
  const byRank = new Map<Rank, T[]>();
  for (const rank of RANK_ORDER) byRank.set(rank, []);
  for (const item of items) {
    (byRank.get(item.player.rank) ?? byRank.get('R1')!).push(item);
  }
  return RANK_ORDER.map((rank) => ({
    rank,
    rows: (byRank.get(rank) ?? []).slice().sort((a, b) => thpOf(b) - thpOf(a)),
  }));
}

function buildGameExportText(
  eventType: EventType,
  slot: SlotKey,
  participations: Participation[],
): string {
  const inSlot = participations.filter((p) => p.assignedSlot === slot);
  const groups = groupByRankByThp(inSlot);
  const heading = `${EVENT_TYPE_SHORT[eventType]} ${slot} — Roster (by rank, THP desc)`;
  const blocks = groups
    .filter((g) => g.rows.length > 0)
    .map((g) => [g.rank, ...g.rows.map((p) => playerLabel(p))].join('\n'));
  return [heading, '', ...blocks].join('\n\n');
}

export default function EventDetail({ user }: { user: AuthUser }) {
  const { id: idStr } = useParams();
  const id = Number(idStr);
  const editable = canEdit(user.role);
  const qc = useQueryClient();

  const eventQuery = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.getEvent(id),
    enabled: !Number.isNaN(id),
  });
  const participationsQuery = useQuery({
    queryKey: ['participations', id],
    queryFn: () => api.listParticipations(id),
    enabled: !Number.isNaN(id),
  });

  const setStatus = useMutation({
    mutationFn: (status: EventStatus) => api.patchEvent(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', id] });
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
  const setPlanningDone = useMutation({
    mutationFn: (planningDone: boolean) => api.patchEvent(id, { planningDone }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', id] });
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });

  const [importOpen, setImportOpen] = useState(false);
  const [openImportJobId, setOpenImportJobId] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const importJobsQuery = useQuery({
    queryKey: ['event-import-jobs', id],
    queryFn: () => api.listEventImportJobs(id),
    refetchInterval: (q) => {
      const pending = q.state.data?.jobs.some(
        (j) => j.status === 'queued' || j.status === 'running',
      );
      return pending ? 2500 : false;
    },
  });
  const importJobs = (importJobsQuery.data?.jobs ?? []).filter((j) => !j.appliedAt);
  const [mobileSlot, setMobileSlot] = useState<SlotKey>('A');

  if (eventQuery.isLoading) return <div className="text-slate-500">Loading…</div>;
  const event = eventQuery.data;
  if (!event) return <div className="text-slate-500">Event not found.</div>;
  if (event.type === 'special_ingame') {
    return <SpecialEventDetail user={user} id={id} />;
  }

  const participations = participationsQuery.data ?? [];
  const eventIsPast = event.eventDate <= todayISO();

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Link
          to="/events"
          className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-sm text-slate-400 hover:bg-slate-800 hover:text-white"
        >
          <ChevronLeft size={14} /> Events
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{EVENT_TYPE_LABEL[event.type]}</h1>
        <span className="text-slate-400">{event.eventDate}</span>
        <span className={cn('rounded px-2 py-0.5 text-xs capitalize', STATUS_STYLES[event.status])}>
          {event.status}
        </span>
        {event.planningDoneAt && (
          <span
            title={`Planning marked done ${event.planningDoneAt.slice(0, 16).replace('T', ' ')}${
              event.planningDoneByName ? ` by ${event.planningDoneByName}` : ''
            }`}
            className="inline-flex items-center gap-1 rounded bg-emerald-900/40 px-2 py-0.5 text-xs font-medium text-emerald-200"
          >
            <CheckCircle2 size={12} /> Planning done
            {event.planningDoneByName && (
              <span className="text-emerald-300/80">· {event.planningDoneByName}</span>
            )}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {editable && event.status === 'planned' && (
            <button
              onClick={() => setPlanningDone.mutate(!event.planningDoneAt)}
              disabled={setPlanningDone.isPending}
              className={cn(
                'inline-flex items-center gap-1 rounded border px-2 py-1 text-sm disabled:opacity-50',
                event.planningDoneAt
                  ? 'border-emerald-700 bg-emerald-900/40 text-emerald-100 hover:bg-emerald-800/60'
                  : 'border-slate-700 hover:bg-slate-800',
              )}
              title={
                event.planningDoneAt
                  ? 'Reopen planning'
                  : 'Mark this event as fully planned'
              }
            >
              <CheckCircle2 size={14} />
              {event.planningDoneAt ? 'Reopen planning' : 'Planning done'}
            </button>
          )}
          {editable && event.status !== 'skipped' && (
            <button
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800"
              title="Import signups from a screen recording"
            >
              <Upload size={14} /> Import signups
            </button>
          )}
          <button
            onClick={() => setExportOpen(true)}
            className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800"
            title="Export assigned starters & subs as CSV"
          >
            <Download size={14} /> Export
          </button>
          {editable && event.status !== 'skipped' && (
            <button
              onClick={() => {
                if (confirm('Mark this event as skipped?')) setStatus.mutate('skipped');
              }}
              className="rounded border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800"
            >
              Skip
            </button>
          )}
          {editable && event.status === 'skipped' && (
            <button
              onClick={() => setStatus.mutate('planned')}
              className="rounded border border-slate-700 px-2 py-1 text-sm hover:bg-slate-800"
            >
              Un-skip
            </button>
          )}
        </div>
      </div>

      {event.status !== 'skipped' && (
        <EventWorkflowSteps
          event={event}
          participations={participations}
          eventIsPast={eventIsPast}
        />
      )}

      {importJobs.length > 0 && editable && (
        <EventImportJobsStrip
          eventId={id}
          jobs={importJobs}
          onOpen={setOpenImportJobId}
        />
      )}

      {editable && event.status !== 'skipped' && (
        <EventBulkActions event={event} participations={participations} />
      )}

      <div className="flex gap-1 rounded border border-slate-800 bg-slate-900/40 p-1 lg:hidden">
        {(['A', 'B'] as SlotKey[]).map((slot) => {
          const info = event.slots.find((s) => s.slotKey === slot);
          const count = participations.filter((p) => p.assignedSlot === slot).length;
          const active = mobileSlot === slot;
          return (
            <button
              key={slot}
              onClick={() => setMobileSlot(slot)}
              className={cn(
                'flex-1 rounded px-3 py-1.5 text-sm font-medium transition',
                active
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200',
              )}
            >
              Slot {slot}
              {info?.startsAtServer && (
                <span className="ml-1 text-xs font-normal text-slate-500">
                  @ {info.startsAtServer}
                </span>
              )}
              <span className="ml-2 text-xs font-normal text-slate-500">({count})</span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {(['A', 'B'] as SlotKey[]).map((slot) => (
          <div key={slot} className={cn(mobileSlot === slot ? '' : 'hidden', 'lg:block')}>
            <SlotPanel
              event={event}
              slot={slot}
              participations={participations}
              editable={editable && event.status !== 'skipped'}
              candidatesCollapsed={!!event.planningDoneAt}
            />
          </div>
        ))}
      </div>

      {eventIsPast && event.status !== 'skipped' && (
        <AttendancePanel eventId={id} participations={participations} editable={editable} />
      )}

      <ChangeLogSection eventId={id} eventType={event.type} />


      <ImportSignupsDialog
        eventId={id}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          qc.invalidateQueries({ queryKey: ['participations', id] });
          qc.invalidateQueries({ queryKey: ['candidates', id] });
          qc.invalidateQueries({ queryKey: ['event', id] });
          qc.invalidateQueries({ queryKey: ['events'] });
          qc.invalidateQueries({ queryKey: ['event-changelog', id] });
          qc.invalidateQueries({ queryKey: ['event-import-jobs', id] });
        }}
      />
      {openImportJobId && (
        <ImportSignupsDialog
          eventId={id}
          open={true}
          initialVideoJobId={openImportJobId}
          onClose={() => setOpenImportJobId(null)}
          onImported={() => {
            qc.invalidateQueries({ queryKey: ['participations', id] });
            qc.invalidateQueries({ queryKey: ['candidates', id] });
            qc.invalidateQueries({ queryKey: ['event', id] });
            qc.invalidateQueries({ queryKey: ['event-changelog', id] });
            qc.invalidateQueries({ queryKey: ['event-import-jobs', id] });
          }}
        />
      )}
      {exportOpen && (
        <ExportDialog
          participations={participations}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
}

function EventWorkflowSteps({
  event,
  participations,
  eventIsPast,
}: {
  event: EventSummary;
  participations: Participation[];
  eventIsPast: boolean;
}) {
  const anyApplied = participations.some((p) => p.appliedA || p.appliedB);
  const anyAssigned = participations.some((p) => p.assignedRole != null);
  const planningDone = !!event.planningDoneAt;
  const assigned = participations.filter((p) => p.assignedRole != null);
  const attendanceComplete =
    assigned.length > 0 && assigned.every((p) => p.attendanceStatus != null);

  const steps: { label: string; done: boolean; active: boolean }[] = [
    { label: 'Event created', done: true, active: false },
    { label: 'Candidates', done: anyApplied, active: !anyApplied },
    { label: 'Slots filled', done: anyAssigned, active: anyApplied && !anyAssigned },
    {
      label: 'Planning done',
      done: planningDone,
      active: anyAssigned && !planningDone,
    },
    {
      label: 'Attendance',
      done: attendanceComplete,
      active: eventIsPast && planningDone && !attendanceComplete,
    },
  ];

  return (
    <ol className="flex flex-wrap items-center gap-1 rounded border border-slate-800 bg-slate-950/40 px-2 py-1.5 text-[11px]">
      {steps.map((s, i) => (
        <li key={s.label} className="flex items-center gap-1">
          <span
            className={cn(
              'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold',
              s.done
                ? 'bg-emerald-700/60 text-emerald-100'
                : s.active
                  ? 'bg-amber-700/60 text-amber-100'
                  : 'bg-slate-800 text-slate-500',
            )}
          >
            {s.done ? '✓' : i + 1}
          </span>
          <span
            className={cn(
              s.done
                ? 'text-slate-300'
                : s.active
                  ? 'font-medium text-amber-200'
                  : 'text-slate-500',
            )}
          >
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <span className="mx-1 text-slate-700">›</span>
          )}
        </li>
      ))}
    </ol>
  );
}

function EventBulkActions({
  event,
  participations,
}: {
  event: EventSummary;
  participations: Participation[];
}) {
  const qc = useQueryClient();
  const policy = useScorePolicy();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['participations', event.id] });
    qc.invalidateQueries({ queryKey: ['candidates', event.id] });
    qc.invalidateQueries({ queryKey: ['event-changelog', event.id] });
  };

  const candidatesA = useQuery({
    queryKey: ['candidates', event.id, 'A'],
    queryFn: () => api.candidates(event.id, 'A'),
  });
  const candidatesB = useQuery({
    queryKey: ['candidates', event.id, 'B'],
    queryFn: () => api.candidates(event.id, 'B'),
  });

  const [pendingPlan, setPendingPlan] = useState<AutofillPlan | null>(null);

  const openDistributeEven = () => {
    const plan = planDistributeEven({
      eventType: event.type as 'canyon_storm' | 'desert_storm',
      candidatesA: candidatesA.data ?? [],
      candidatesB: candidatesB.data ?? [],
      participations,
      policy,
    });
    setPendingPlan(plan);
  };

  const openCombinedAutofill = (modeA: AutofillMode, modeB: AutofillMode) => {
    const startersFor = (slot: SlotKey) =>
      participations.filter(
        (p) => p.assignedSlot === slot && p.assignedRole === 'starter',
      ).length;
    const subsFor = (slot: SlotKey) =>
      participations.filter(
        (p) => p.assignedSlot === slot && p.assignedRole === 'sub',
      ).length;

    // Both slots are filled strongest-first. The "weak" slot is only weak in
    // the sense that it fills last — it gets whichever applicants the primary
    // slot didn't already claim, not the globally weakest players.
    const primary: SlotKey = modeA === 'strong' ? 'A' : 'B';
    const secondary: SlotKey = primary === 'A' ? 'B' : 'A';
    const candidatesFor = (slot: SlotKey) =>
      (slot === 'A' ? candidatesA.data : candidatesB.data) ?? [];

    const planPrimary = planSlotAutofill({
      mode: 'strong',
      slot: primary,
      candidates: candidatesFor(primary),
      startersAssigned: startersFor(primary),
      subsAssigned: subsFor(primary),
      policy,
    });
    const pickedPrimary = new Set(
      planPrimary.picks.map((p) => p.candidate.player.id),
    );
    const secondaryCandidates = candidatesFor(secondary).filter(
      (c) => !pickedPrimary.has(c.player.id),
    );
    const planSecondary = planSlotAutofill({
      mode: 'strong',
      slot: secondary,
      candidates: secondaryCandidates,
      startersAssigned: startersFor(secondary),
      subsAssigned: subsFor(secondary),
      policy,
    });

    const skippedSeen = new Set<number>();
    const skipped = [...planPrimary.skipped, ...planSecondary.skipped].filter(
      (s) => {
        const id = s.candidate.player.id;
        if (skippedSeen.has(id)) return false;
        skippedSeen.add(id);
        return true;
      },
    );

    setPendingPlan({
      action: modeA === 'strong' ? 'strong_a_weak_b' : 'weak_a_strong_b',
      picks: [...planPrimary.picks, ...planSecondary.picks],
      skipped,
    });
  };

  const applyPlan = useMutation({
    mutationFn: async (plan: AutofillPlan) => {
      const rows = plan.picks.map((p) => ({
        playerId: p.candidate.player.id,
        assignedSlot: p.slot,
        assignedRole: p.role,
        assignedPosition: p.position,
      }));
      if (rows.length) {
        await api.bulkParticipations(event.id, rows, `autofill_${plan.action}`);
      }
    },
    onSuccess: () => {
      invalidate();
      setPendingPlan(null);
    },
  });

  const copyAToB = useMutation({
    mutationFn: async () => {
      const rows = participations
        .filter((p) => p.appliedA && !p.appliedB)
        .map((p) => ({ playerId: p.playerId, appliedB: true }));
      if (rows.length) await api.bulkParticipations(event.id, rows, 'copy_a_to_b');
    },
    onSuccess: invalidate,
  });

  const copyCount = participations.filter((p) => p.appliedA && !p.appliedB).length;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-slate-800 bg-slate-950/40 px-3 py-2 text-xs text-slate-400">
      <span className="uppercase tracking-wider text-slate-500">Autofill</span>
      <button
        onClick={openDistributeEven}
        disabled={applyPlan.isPending}
        className="inline-flex items-center gap-1 rounded border border-emerald-700 px-2 py-1 text-emerald-200 hover:bg-emerald-900/30 disabled:opacity-50"
        title="Preview a plan that distributes applicants zig-zag by score across both slots (respects each player's registration)"
      >
        <Sparkles size={12} /> Even both slots
      </button>
      <button
        onClick={() => openCombinedAutofill('strong', 'weak')}
        disabled={applyPlan.isPending}
        className="inline-flex items-center gap-1 rounded border border-red-700 px-2 py-1 text-red-200 hover:bg-red-900/30 disabled:opacity-50"
        title="Preview a plan that picks strongest applicants for Slot A and weakest for Slot B (R4/R5 and Never-Sub always picked)"
      >
        <Sparkles size={12} /> Strong A, Weak B
      </button>
      <button
        onClick={() => openCombinedAutofill('weak', 'strong')}
        disabled={applyPlan.isPending}
        className="inline-flex items-center gap-1 rounded border border-sky-700 px-2 py-1 text-sky-200 hover:bg-sky-900/30 disabled:opacity-50"
        title="Preview a plan that picks weakest applicants for Slot A and strongest for Slot B (R4/R5 and Never-Sub always picked)"
      >
        <Sparkles size={12} /> Weak A, Strong B
      </button>
      {event.type === 'canyon_storm' && (
        <button
          onClick={() => copyAToB.mutate()}
          disabled={copyAToB.isPending || copyCount === 0}
          className="inline-flex items-center gap-1 rounded border border-indigo-700 px-2 py-1 text-indigo-200 hover:bg-indigo-900/30 disabled:opacity-50"
          title="Also register every Slot A applicant for Slot B (existing B applications untouched)"
        >
          <ArrowLeftRight size={12} /> Copy A → B ({copyCount})
        </button>
      )}
      {pendingPlan && (
        <AutofillPreviewModal
          plan={pendingPlan}
          isPending={applyPlan.isPending}
          onCancel={() => setPendingPlan(null)}
          onConfirm={() => applyPlan.mutate(pendingPlan)}
          eventType={event.type}
          slotTimes={{
            A: event.slots.find((s) => s.slotKey === 'A')?.startsAtServer ?? undefined,
            B: event.slots.find((s) => s.slotKey === 'B')?.startsAtServer ?? undefined,
          }}
          participations={participations}
        />
      )}
    </div>
  );
}

function SlotPanel({
  event,
  slot,
  participations,
  editable,
  candidatesCollapsed = false,
}: {
  event: EventSummary;
  slot: SlotKey;
  participations: Participation[];
  editable: boolean;
  candidatesCollapsed?: boolean;
}) {
  const slotInfo = event.slots.find((s) => s.slotKey === slot);
  const slotATime = event.slots.find((s) => s.slotKey === 'A')?.startsAtServer;
  const slotBTime = event.slots.find((s) => s.slotKey === 'B')?.startsAtServer;
  const slotsSameTime = !!slotATime && !!slotBTime && slotATime === slotBTime;
  const inThisSlot = participations.filter((p) => p.assignedSlot === slot);
  const qc = useQueryClient();
  const clearSlot = useMutation({
    mutationFn: async () => {
      if (inThisSlot.length === 0) return;
      await api.bulkParticipations(
        event.id,
        inThisSlot.map((p) => ({
          playerId: p.playerId,
          assignedSlot: null,
          assignedRole: null,
          assignedPosition: null,
        })),
        `clear_slot_${slot.toLowerCase()}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['participations', event.id] });
      qc.invalidateQueries({ queryKey: ['candidates', event.id] });
    qc.invalidateQueries({ queryKey: ['event-changelog', event.id] });
    },
  });
  const byM1Desc = (a: Participation, b: Participation) =>
    (b.player.latestSnapshot?.m1Power ?? 0) - (a.player.latestSnapshot?.m1Power ?? 0);
  const starters = inThisSlot.filter((p) => p.assignedRole === 'starter').sort(byM1Desc);
  const subs = inThisSlot.filter((p) => p.assignedRole === 'sub').sort(byM1Desc);
  const m1RankMap = new Map<number, number>();
  [...starters, ...subs]
    .slice()
    .sort(
      (a, b) =>
        (b.player.latestSnapshot?.m1Power ?? 0) -
        (a.player.latestSnapshot?.m1Power ?? 0),
    )
    .forEach((p, i) => m1RankMap.set(p.playerId, i + 1));
  const appliedHere = participations.filter(
    (p) => (slot === 'A' ? p.appliedA : p.appliedB) && p.assignedSlot !== slot,
  );
  const startersOver = starters.length > 20;
  const subsOver = subs.length > 10;

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <header className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold">
            Slot {slot} <span className="text-sm font-normal text-slate-400">@ {slotInfo?.startsAtServer}</span>
          </h2>
          <p className="text-xs text-slate-500">
            <span className={cn(startersOver && 'font-semibold text-red-400')}>
              {starters.length}/20 starters
            </span>
            {' · '}
            <span className={cn(subsOver && 'font-semibold text-red-400')}>
              {subs.length}/10 subs
            </span>
            {' · '}
            {appliedHere.length} applied here & unassigned
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {subs.length > 0 && (
            <CopyDmButton
              eventType={event.type}
              slot={slot}
              startsAtServer={slotInfo?.startsAtServer}
              starters={starters}
              subs={subs}
            />
          )}
          {inThisSlot.length > 0 && (
            <CopyGameExportButton
              eventType={event.type}
              slot={slot}
              participations={participations}
            />
          )}
          {editable && inThisSlot.length > 0 && (
            <button
              onClick={() => {
                if (confirm(`Clear all ${inThisSlot.length} assignments in slot ${slot}?`)) {
                  clearSlot.mutate();
                }
              }}
              disabled={clearSlot.isPending}
              className="inline-flex items-center gap-1 rounded border border-red-800 px-2 py-1 text-xs text-red-300 hover:bg-red-900/30 disabled:opacity-50"
              title={`Unassign every player from slot ${slot}`}
            >
              <Trash2 size={12} /> Clear slot
            </button>
          )}
          {event.type === 'canyon_storm' && (
            <>
              <Link
                to={`/plans/cs?eventId=${event.id}&slot=${slot}&variant=rulebringers`}
                className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
                title="Open Rulebringers battle plan for this slot"
              >
                Rulebringers →
              </Link>
              <Link
                to={`/plans/cs?eventId=${event.id}&slot=${slot}&variant=dawnbreakers&side=left`}
                className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
                title="Open Dawnbreakers battle plan for this slot"
              >
                Dawnbreakers →
              </Link>
            </>
          )}
          {event.type === 'desert_storm' && (
            <Link
              to={`/plans/ds?eventId=${event.id}&slot=${slot}&variant=desertstorm`}
              className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
              title="Open two-phase Desert Storm battle plan for this slot"
            >
              DS Plan →
            </Link>
          )}
        </div>
      </header>

      {candidatesCollapsed ? (
        <details className="rounded border border-slate-800 bg-slate-950/40">
          <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-wider text-slate-400 hover:bg-slate-900/40">
            Assigned players
            <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-slate-500">
              ({starters.length} starters · {subs.length} subs — click to expand)
            </span>
          </summary>
          <div className="space-y-3 px-3 pb-3 pt-1">
            <AssignedList
              label="Starters"
              role="starter"
              target={20}
              rows={starters}
              counterpartCount={subs.length}
              eventId={event.id}
              slot={slot}
              editable={editable}
              m1RankMap={m1RankMap}
              slotsSameTime={slotsSameTime}
            />
            <AssignedList
              label="Subs"
              role="sub"
              target={10}
              rows={subs}
              counterpartCount={starters.length}
              eventId={event.id}
              slot={slot}
              editable={editable}
              m1RankMap={m1RankMap}
              slotsSameTime={slotsSameTime}
            />
          </div>
        </details>
      ) : (
        <div className="space-y-3">
          <AssignedList
            label="Starters"
            role="starter"
            target={20}
            rows={starters}
            counterpartCount={subs.length}
            eventId={event.id}
            slot={slot}
            editable={editable}
            m1RankMap={m1RankMap}
            slotsSameTime={slotsSameTime}
          />
          <AssignedList
            label="Subs"
            role="sub"
            target={10}
            rows={subs}
            counterpartCount={starters.length}
            eventId={event.id}
            slot={slot}
            editable={editable}
            m1RankMap={m1RankMap}
            slotsSameTime={slotsSameTime}
          />
        </div>
      )}

      {editable && (
        candidatesCollapsed ? (
          <details className="mt-4 rounded border border-slate-800 bg-slate-950/40">
            <summary className="cursor-pointer px-3 py-2 text-xs uppercase tracking-wider text-slate-400 hover:bg-slate-900/40">
              Candidates
              <span className="ml-2 text-[10px] font-normal normal-case tracking-normal text-slate-500">
                (planning done — click to expand)
              </span>
            </summary>
            <div className="px-3 pb-3">
              <CandidatesPanel eventId={event.id} slot={slot} />
            </div>
          </details>
        ) : (
          <CandidatesPanel eventId={event.id} slot={slot} />
        )
      )}
    </section>
  );
}

function AssignedList({
  label,
  role,
  target,
  rows,
  counterpartCount,
  eventId,
  slot,
  editable,
  m1RankMap,
  slotsSameTime,
}: {
  label: string;
  role: AssignedRole;
  target: number;
  rows: Participation[];
  counterpartCount: number;
  eventId: number;
  slot: SlotKey;
  editable: boolean;
  m1RankMap: Map<number, number>;
  slotsSameTime: boolean;
}) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['participations', eventId] });
    qc.invalidateQueries({ queryKey: ['candidates', eventId] });
    qc.invalidateQueries({ queryKey: ['event-changelog', eventId] });
  };
  const unassign = useMutation({
    mutationFn: (playerId: number) =>
      api.patchParticipation(eventId, playerId, {
        assignedSlot: null,
        assignedRole: null,
        assignedPosition: null,
      }),
    onSuccess: invalidate,
  });
  const swapRole = useMutation({
    mutationFn: (p: Participation) => {
      const newRole: AssignedRole = role === 'starter' ? 'sub' : 'starter';
      return api.patchParticipation(eventId, p.playerId, {
        assignedSlot: slot,
        assignedRole: newRole,
        assignedPosition: counterpartCount + 1,
      });
    },
    onSuccess: invalidate,
  });
  const toggleFlex = useMutation({
    mutationFn: (p: Participation) =>
      api.patchParticipation(eventId, p.playerId, { flex: !p.flex }),
    onSuccess: invalidate,
  });

  const over = rows.length > target;
  const sumM1 = rows.reduce(
    (acc, r) => acc + (r.player.latestSnapshot?.m1Power ?? 0),
    0,
  );
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-400">
          {label}{' '}
          <span className={cn(over ? 'font-semibold text-red-400' : 'text-slate-500')}>
            ({rows.length}/{target}
            {over && ` · +${rows.length - target} over`})
          </span>
        </span>
        <span className="text-xs tabular-nums text-slate-400" title="Sum of M1 Power">
          Σ M1 {formatPower(sumM1)}
        </span>
      </div>
      <ul className="space-y-1">
        {rows.length === 0 && (
          <li className="rounded border border-dashed border-slate-800 px-3 py-2 text-xs text-slate-600">
            No {label.toLowerCase()} assigned yet.
          </li>
        )}
        {rows.map((p, i) => (
          <li
            key={p.id}
            className="flex flex-wrap items-center gap-1.5 rounded bg-slate-950/60 px-2 py-1 text-xs sm:flex-nowrap sm:gap-2 sm:text-sm"
          >
            <span className="w-6 text-xs tabular-nums text-slate-500">{i + 1}</span>
            {(() => {
              const rank = m1RankMap.get(p.playerId);
              const tier =
                rank == null
                  ? 'border-slate-700 bg-slate-800/60 text-slate-400'
                  : rank <= 10
                    ? 'border-red-500/60 bg-red-500/15 text-red-300'
                    : rank <= 20
                      ? 'border-orange-500/60 bg-orange-500/15 text-orange-300'
                      : 'border-yellow-500/50 bg-yellow-500/10 text-yellow-300';
              return (
                <span
                  className={cn(
                    'w-7 shrink-0 rounded border px-1 text-center text-[10px] font-semibold tabular-nums',
                    tier,
                  )}
                  title="M1 strength rank across this slot (1–30, strongest first)"
                >
                  {rank ?? '–'}
                </span>
              );
            })()}
            <PlayerLabel
              player={p.player}
              className="min-w-0 flex-1 sm:flex-initial"
              nameClassName={
                (p.metrics?.consecutiveNotPicked ?? 0) >= 1
                  ? 'text-orange-300 font-semibold'
                  : undefined
              }
            />
            <span className="tabular-nums text-xs text-slate-300" title="M1 Power">
              {formatPower(p.player.latestSnapshot?.m1Power)}
            </span>
            <span className="text-xs text-slate-500">{p.player.rank}</span>
            <div className="flex w-full flex-wrap items-center justify-end gap-1.5 sm:w-auto sm:flex-1 sm:contents">
              <RecentEventsChip events={p.metrics?.recentEvents ?? []} />
              <PlayerFlagChip
                playerId={p.player.id}
                flags={p.player.activeFlags ?? []}
                editable={editable}
              />
              <LeaveMarker leave={p.player.currentLeave} />
              <span className="hidden flex-1 sm:block" />
              <AppliedBadge
                appliedA={p.appliedA}
                appliedB={p.appliedB}
                assignedSlot={p.assignedSlot}
                slotsSameTime={slotsSameTime}
              />
              <button
                onClick={() => editable && toggleFlex.mutate(p)}
                disabled={!editable}
                title={
                  editable
                    ? p.flex
                      ? 'Mark as fixed'
                      : 'Mark as flex'
                    : p.flex
                      ? 'Flex'
                      : 'Fixed'
                }
                className={cn(
                  'rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition',
                  p.flex
                    ? 'border-fuchsia-700 bg-fuchsia-900/40 text-fuchsia-200'
                    : 'border-slate-700 text-slate-500',
                  editable && 'hover:bg-slate-800',
                  !editable && 'cursor-default',
                )}
              >
                {p.flex ? 'Flex' : 'Fixed'}
              </button>
              {editable && (
                <>
                  <button
                    onClick={() => swapRole.mutate(p)}
                    className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-indigo-300"
                    title={role === 'starter' ? 'Move to subs' : 'Promote to starter'}
                  >
                    {role === 'starter' ? (
                      <ArrowDownToLine size={12} />
                    ) : (
                      <ArrowUpToLine size={12} />
                    )}
                  </button>
                  <button
                    onClick={() => unassign.mutate(p.playerId)}
                    className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-red-400"
                    title="Unassign"
                  >
                    <UserMinus size={12} />
                  </button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CandidatesPanel({ eventId, slot }: { eventId: number; slot: SlotKey }) {
  const [search, setSearch] = useState('');
  const [onlyApplied, setOnlyApplied] = useState(true);
  const [onlyNotApplied, setOnlyNotApplied] = useState(false);
  const [notInSlot, setNotInSlot] = useState(true);
  const [gameSort, setGameSort] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [defaultsInit, setDefaultsInit] = useState(false);
  const qc = useQueryClient();
  const policy = useScorePolicy();

  const candidatesQuery = useQuery({
    queryKey: ['candidates', eventId, slot],
    queryFn: () => api.candidates(eventId, slot),
  });

  // First time candidates load: if nobody applied yet, flip the defaults to
  // "Not applied" + in-game sort so the panel is ready for manual selection
  // from the full roster. Subsequent user toggles are preserved.
  useEffect(() => {
    if (defaultsInit) return;
    const rows = candidatesQuery.data;
    if (!rows) return;
    const anyApplied = rows.some((c) => c.appliedThisSlot || c.appliedOtherSlot);
    if (!anyApplied) {
      setOnlyApplied(false);
      setOnlyNotApplied(true);
      setNotInSlot(false);
      setGameSort(true);
    }
    setDefaultsInit(true);
  }, [candidatesQuery.data, defaultsInit]);
  const participations = qc.getQueryData<Participation[]>(['participations', eventId]) ?? [];
  const startersAssignedHere = participations.filter(
    (p) => p.assignedSlot === slot && p.assignedRole === 'starter',
  ).length;
  const subsAssignedHere = participations.filter(
    (p) => p.assignedSlot === slot && p.assignedRole === 'sub',
  ).length;

  const filtered = useMemo(() => {
    const all = candidatesQuery.data ?? [];
    const kept = all.filter((c) => {
      if (onlyApplied && !c.appliedThisSlot) return false;
      if (onlyNotApplied && (c.appliedThisSlot || c.appliedOtherSlot)) return false;
      if (notInSlot && (c.assignedHere || c.assignedOther)) return false;
      if (search && !c.player.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
    if (!gameSort) return kept;
    return groupByRankByThp(kept).flatMap((g) => g.rows);
  }, [candidatesQuery.data, search, onlyApplied, onlyNotApplied, notInSlot, gameSort]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['participations', eventId] });
    qc.invalidateQueries({ queryKey: ['candidates', eventId] });
    qc.invalidateQueries({ queryKey: ['event-changelog', eventId] });
  };

  const appliedKey: 'appliedA' | 'appliedB' = slot === 'A' ? 'appliedA' : 'appliedB';

  const toggleAppliedFor = useMutation({
    mutationFn: async ({ c, which }: { c: Candidate; which: SlotKey }) => {
      const key: 'appliedA' | 'appliedB' = which === 'A' ? 'appliedA' : 'appliedB';
      const current = which === slot ? c.appliedThisSlot : c.appliedOtherSlot;
      await api.bulkParticipations(eventId, [
        { playerId: c.player.id, [key]: !current },
      ]);
    },
    onSuccess: invalidate,
  });

  const assignAs = useMutation({
    mutationFn: async ({ candidate, role }: { candidate: Candidate; role: AssignedRole }) => {
      const position =
        role === 'starter' ? startersAssignedHere + 1 : subsAssignedHere + 1;
      await api.bulkParticipations(eventId, [
        {
          playerId: candidate.player.id,
          assignedSlot: slot,
          assignedRole: role,
          assignedPosition: position,
        },
      ]);
    },
    onSuccess: invalidate,
  });

  return (
    <div className="mt-4 rounded border border-slate-800 bg-slate-950/40 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-slate-400">Candidates</span>
        <div className="flex gap-1">
          <button
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
            title="Paste a list of player names from in-game to mark them as applied"
          >
            <ClipboardPaste size={12} /> Paste applications
          </button>
        </div>
      </div>
      <div className="mb-2 flex flex-wrap gap-2 text-[10px] text-slate-500">
        <Legend icon={<Star size={10} className="text-amber-400" />} label="Never-Sub" />
        <Legend
          icon={<span className="font-semibold text-orange-300">Name</span>}
          label="Overdue for a pick"
        />
        <Legend
          icon={
            <span className="inline-flex items-center rounded bg-emerald-700/70 px-1 py-[2px] text-emerald-100">
              ✓
            </span>
          }
          label="Picked"
        />
        <Legend
          icon={
            <span className="inline-flex items-center rounded bg-sky-700/70 px-1 py-[2px] text-sky-100">
              ✓
            </span>
          }
          label="Picked as sub"
        />
        <Legend
          icon={
            <span className="inline-flex items-center rounded bg-red-800/70 px-1 py-[2px] text-red-100">
              ×
            </span>
          }
          label="Applied, not picked"
        />
        <Legend
          icon={
            <span className="inline-flex items-center rounded bg-slate-700/70 px-1 py-[2px] text-slate-300">
              ○
            </span>
          }
          label="Not registered"
        />
        <Legend
          icon={
            <span
              className="inline-flex items-center rounded px-1 py-[2px] text-white"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(45deg, rgb(153 27 27 / 0.9) 0 4px, rgb(4 120 87 / 0.7) 4px 8px)',
              }}
            >
              <Ban size={11} strokeWidth={3.5} />
            </span>
          }
          label="Picked but no-show"
        />
        <span className="text-slate-600">|</span>
        <Legend
          icon={<span className="rounded bg-emerald-700/50 px-1 text-[9px] text-emerald-100">A</span>}
          label="Applied here"
        />
        <Legend
          icon={<span className="rounded bg-amber-700/40 px-1 text-[9px] text-amber-200">B</span>}
          label="Applied other slot"
        />
        <Legend
          icon={<span className="rounded bg-slate-800 px-1 text-[9px] text-slate-500">A</span>}
          label="Not applied"
        />
      </div>
      <div className="mb-2 flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-full rounded border border-slate-800 bg-slate-950 py-1 pl-7 pr-2 text-sm"
          />
        </div>
        <label className="flex items-center gap-1 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={onlyApplied}
            onChange={(e) => {
              setOnlyApplied(e.target.checked);
              if (e.target.checked) setOnlyNotApplied(false);
            }}
          />
          Applied only
        </label>
        <label
          className="flex items-center gap-1 text-xs text-slate-400"
          title="Show only players who applied to neither slot"
        >
          <input
            type="checkbox"
            checked={onlyNotApplied}
            onChange={(e) => {
              setOnlyNotApplied(e.target.checked);
              if (e.target.checked) setOnlyApplied(false);
            }}
          />
          Not applied
        </label>
        <label
          className="flex items-center gap-1 text-xs text-slate-400"
          title="Hide players already assigned to this slot"
        >
          <input
            type="checkbox"
            checked={notInSlot}
            onChange={(e) => setNotInSlot(e.target.checked)}
          />
          Not in slot
        </label>
        <label
          className="flex items-center gap-1 text-xs text-slate-400"
          title="Sort like in-game: group by R5→R1, THP desc within each rank. Filters still apply."
        >
          <input
            type="checkbox"
            checked={gameSort}
            onChange={(e) => setGameSort(e.target.checked)}
          />
          Sort like in-game
        </label>
      </div>

      <ul className="max-h-80 space-y-1 overflow-y-auto pr-1">
        {candidatesQuery.isLoading && (
          <li className="text-xs text-slate-500">Loading candidates…</li>
        )}
        {filtered.length === 0 && !candidatesQuery.isLoading && (
          <li className="text-xs text-slate-500">No candidates match.</li>
        )}
        {filtered.map((c) => (
          <CandidateRow
            key={c.player.id}
            candidate={c}
            slot={slot}
            onAssign={(role) => assignAs.mutate({ candidate: c, role })}
            onToggleApplied={(which) => toggleAppliedFor.mutate({ c, which })}
          />
        ))}
      </ul>

      {bulkOpen && (
        <BulkApplicationsModal
          eventId={eventId}
          slot={slot}
          candidates={candidatesQuery.data ?? []}
          onClose={() => setBulkOpen(false)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}

function BulkApplicationsModal({
  eventId,
  slot,
  candidates,
  onClose,
  onSaved,
}: {
  eventId: number;
  slot: SlotKey;
  candidates: Candidate[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [text, setText] = useState('');
  const [replaceMode, setReplaceMode] = useState(false);
  const names = text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  const matched: Candidate[] = [];
  const unmatched: string[] = [];
  const byLowerName = new Map(candidates.map((c) => [c.player.name.toLowerCase(), c]));
  for (const n of names) {
    const c = byLowerName.get(n.toLowerCase());
    if (c) matched.push(c);
    else unmatched.push(n);
  }
  const matchedIds = new Set(matched.map((c) => c.player.id));

  const appliedKey: 'appliedA' | 'appliedB' = slot === 'A' ? 'appliedA' : 'appliedB';
  const save = useMutation({
    mutationFn: async () => {
      const rows: Parameters<typeof api.bulkParticipations>[1] = matched.map((c) => ({
        playerId: c.player.id,
        [appliedKey]: true,
      }));
      // In replace mode, unmark anyone previously applied to this slot but not in the new list.
      if (replaceMode) {
        const toUnmark = candidates.filter(
          (c) => c.appliedThisSlot && !matchedIds.has(c.player.id),
        );
        rows.push(
          ...toUnmark.map((c) => ({
            playerId: c.player.id,
            [appliedKey]: false,
          })),
        );
      }
      if (rows.length) {
        await api.bulkParticipations(
          eventId,
          rows,
          `paste_apps_${slot.toLowerCase()}`,
        );
      }
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg space-y-3 rounded-lg border border-slate-800 bg-slate-950 p-5 shadow-2xl">
        <div>
          <h3 className="text-base font-semibold">
            Paste applications for slot {slot}
          </h3>
          <p className="text-xs text-slate-400">
            One player name per line. Names are matched case-insensitively.
          </p>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder={'Khaleesi\nCommander Titties\n…'}
          className="w-full rounded border border-slate-800 bg-slate-950 p-2 font-mono text-sm"
        />
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs uppercase text-emerald-400">
              Matched ({matched.length})
            </div>
            <ul className="mt-1 max-h-32 overflow-y-auto text-xs text-slate-300">
              {matched.map((c) => (
                <li key={c.player.id}>{c.player.name}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase text-red-400">
              Unmatched ({unmatched.length})
            </div>
            <ul className="mt-1 max-h-32 overflow-y-auto text-xs text-slate-400">
              {unmatched.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={replaceMode}
            onChange={(e) => setReplaceMode(e.target.checked)}
          />
          Replace mode — unmark anyone currently applied to slot {slot} who isn't in this list
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={matched.length === 0 || save.isPending}
            className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            {save.isPending
              ? 'Saving…'
              : `Mark ${matched.length} as applied`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Legend({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {icon} {label}
    </span>
  );
}

function AppliedBadge({
  appliedA,
  appliedB,
  assignedSlot,
  slotsSameTime,
}: {
  appliedA: boolean;
  appliedB: boolean;
  assignedSlot: SlotKey | null;
  slotsSameTime: boolean;
}) {
  if (!appliedA && !appliedB) {
    return (
      <span
        title="Did not apply"
        className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-400"
      >
        ⚪
      </span>
    );
  }
  if (appliedA && appliedB) {
    return (
      <span
        title="Applied for both slots"
        className="rounded bg-emerald-700/50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-100"
      >
        {slotsSameTime ? '🟢' : '🟢A+B'}
      </span>
    );
  }
  const appliedSlot: SlotKey = appliedA ? 'A' : 'B';
  if (appliedSlot === assignedSlot) {
    return (
      <span
        title={`Applied for slot ${appliedSlot}`}
        className="rounded bg-emerald-700/50 px-1.5 py-0.5 text-[10px] text-emerald-100"
      >
        🟢
      </span>
    );
  }
  return (
    <span
      title={`Applied for slot ${appliedSlot} — assigned here anyway`}
      className="rounded bg-amber-700/40 px-1.5 py-0.5 text-[10px] text-amber-200"
    >
      🟡{appliedSlot}
    </span>
  );
}

function CandidateRow({
  candidate: c,
  slot,
  onAssign,
  onToggleApplied,
}: {
  candidate: Candidate;
  slot: SlotKey;
  onAssign: (role: AssignedRole) => void;
  onToggleApplied: (which: SlotKey) => void;
}) {
  const policy = useScorePolicy();
  const assignedSomewhere = c.assignedHere || c.assignedOther;
  const otherSlot: SlotKey = slot === 'A' ? 'B' : 'A';
  const appliedAt = (which: SlotKey) =>
    which === slot ? c.appliedThisSlot : c.appliedOtherSlot;
  return (
    <li
      className={cn(
        'flex flex-wrap items-center gap-1.5 rounded px-2 py-1 text-xs sm:flex-nowrap sm:gap-2 sm:text-sm',
        assignedSomewhere ? 'bg-slate-900/40 opacity-60' : 'bg-slate-900/60 hover:bg-slate-800/60',
      )}
    >
      <PlayerLabel
        player={c.player}
        snapshot={c.latestSnapshot}
        className="min-w-0 flex-1 sm:flex-initial"
        nameClassName={
          c.metrics.consecutiveNotPicked >= 1
            ? 'text-orange-300 font-semibold'
            : undefined
        }
      />
      <ScoreBadge breakdown={scoreBreakdown(c, policy, { slot })} />
      <span className="text-xs text-slate-500">{c.player.rank}</span>
      <div className="flex w-full flex-wrap items-center justify-end gap-1.5 sm:w-auto sm:flex-1 sm:contents">
        <RecentEventsChip events={c.metrics.recentEvents ?? []} />
        <PlayerFlagChip
          playerId={c.player.id}
          flags={c.player.activeFlags ?? []}
          editable
        />
        <LeaveMarker leave={c.player.currentLeave} />
        <span className="hidden flex-1 sm:block" />
        <span
          className="hidden text-xs tabular-nums text-slate-400 sm:inline"
          title="M1 Power"
        >
          {formatPower(c.latestSnapshot?.m1Power)}
        </span>
        <div className="flex gap-0.5">
          {([slot, otherSlot] as SlotKey[]).map((s) => {
            const on = appliedAt(s);
            const isThis = s === slot;
            return (
              <button
                key={s}
                onClick={() => onToggleApplied(s)}
                title={
                  on
                    ? `Applied to slot ${s} — click to unmark`
                    : `Click to mark as applied to slot ${s}`
                }
                className={cn(
                  'rounded px-1.5 py-0.5 text-[10px] font-semibold transition',
                  on && isThis && 'bg-emerald-700/50 text-emerald-100 hover:bg-emerald-700/70',
                  on && !isThis && 'bg-amber-700/40 text-amber-200 hover:bg-amber-700/60',
                  !on && 'bg-slate-800 text-slate-500 hover:bg-slate-700',
                )}
              >
                {s}
              </button>
            );
          })}
        </div>
        {!assignedSomewhere ? (
          <div className="flex gap-1">
            <button
              onClick={() => onAssign('starter')}
              className="rounded border border-emerald-700 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-200 hover:bg-emerald-900/30"
              title={`Assign as starter in slot ${slot}`}
            >
              Starter
            </button>
            <button
              onClick={() => onAssign('sub')}
              className="rounded border border-sky-700 px-1.5 py-0.5 text-[10px] font-semibold text-sky-200 hover:bg-sky-900/30"
              title={`Assign as sub in slot ${slot}`}
            >
              Sub
            </button>
          </div>
        ) : (
          <span className="text-xs text-slate-500">
            {c.assignedHere
              ? `assigned ${c.assignedHere}`
              : `in slot ${c.assignedOther}`}
          </span>
        )}
      </div>
    </li>
  );
}

function buildExportCsv(participations: Participation[]) {
  const assigned = participations.filter(
    (p) => p.assignedSlot != null && p.assignedRole != null,
  );
  assigned.sort((a, b) => {
    if (a.assignedSlot !== b.assignedSlot) return (a.assignedSlot ?? '').localeCompare(b.assignedSlot ?? '');
    if (a.assignedRole !== b.assignedRole) return a.assignedRole === 'starter' ? -1 : 1;
    return (a.assignedPosition ?? 0) - (b.assignedPosition ?? 0);
  });
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = ['displayName', 'team', 'position', 'm1Type', 'm1Power', 'flex'];
  const lines = [header.join(',')];
  for (const p of assigned) {
    const name = p.player.displayName?.trim() || p.player.name;
    const snap = p.player.latestSnapshot;
    const row = [
      esc(name),
      esc(p.assignedSlot!),
      esc(p.assignedRole!),
      esc(snap?.m1Type ?? ''),
      esc(snap?.m1Power ?? ''),
      esc(p.flex ? 'flex' : 'fixed'),
    ];
    lines.push(row.join(','));
  }
  return lines.join('\n');
}

function CopyGameExportButton({
  eventType,
  slot,
  participations,
}: {
  eventType: EventType;
  slot: SlotKey;
  participations: Participation[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
        title="Show roster grouped by R5→R1, sorted by THP desc — mirrors the in-game list."
      >
        <Download size={12} /> Export to game
      </button>
      {open && (
        <GameExportDialog
          eventType={eventType}
          slot={slot}
          participations={participations}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function GameExportDialog({
  eventType,
  slot,
  participations,
  onClose,
}: {
  eventType: EventType;
  slot: SlotKey;
  participations: Participation[];
  onClose: () => void;
}) {
  const groups = useMemo(() => {
    const inSlot = participations.filter((p) => p.assignedSlot === slot);
    return groupByRankByThp(inSlot).filter((g) => g.rows.length > 0);
  }, [participations, slot]);
  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  const text = useMemo(
    () => buildGameExportText(eventType, slot, participations),
    [eventType, slot, participations],
  );
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // no-op
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md space-y-3 rounded-lg border border-slate-800 bg-slate-950 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold">
              Export to game · {EVENT_TYPE_SHORT[eventType]} {slot}
            </h3>
            <p className="text-xs text-slate-400">
              {total} players · grouped by rank, THP desc
            </p>
          </div>
          <button
            onClick={onCopy}
            className="inline-flex items-center gap-1 rounded border border-indigo-700 px-2 py-1 text-sm text-indigo-200 hover:bg-indigo-900/30"
          >
            <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        {groups.length === 0 ? (
          <div className="rounded border border-slate-800 bg-slate-900/40 p-4 text-center text-sm text-slate-500">
            No players assigned to this slot yet.
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {groups.map((g) => (
              <div key={g.rank}>
                <div className="mb-1 flex items-baseline justify-between border-b border-slate-800 pb-0.5">
                  <span className="text-xs font-semibold tracking-wider text-orange-400">
                    {g.rank}
                  </span>
                  <span className="text-[10px] text-slate-500">
                    {g.rows.length} {g.rows.length === 1 ? 'player' : 'players'}
                  </span>
                </div>
                <ol className="space-y-0.5">
                  {g.rows.map((p, i) => (
                    <li
                      key={p.playerId}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-5 shrink-0 text-right text-[10px] text-slate-500">
                          {i + 1}.
                        </span>
                        <span className="truncate text-slate-100">
                          {playerLabel(p)}
                        </span>
                        {p.assignedRole === 'sub' && (
                          <span className="rounded bg-slate-800 px-1 text-[9px] uppercase tracking-wider text-slate-400">
                            sub
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 font-mono text-xs text-slate-400">
                        {formatPower(p.player.latestSnapshot?.totalPower)}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyDmButton({
  eventType,
  slot,
  startsAtServer,
  starters,
  subs,
}: {
  eventType: EventType;
  slot: SlotKey;
  startsAtServer: string | undefined;
  starters: Participation[];
  subs: Participation[];
}) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(
    () => buildSlotDmText({ eventType, slot, startsAtServer, starters, subs }),
    [eventType, slot, startsAtServer, starters, subs],
  );
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return (
    <button
      onClick={onCopy}
      className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
      title={`Copy DM text (${text.length}/${DM_TEXT_MAX})\n\n${text}`}
    >
      <Copy size={12} /> {copied ? 'Copied' : 'Copy DM'}
    </button>
  );
}

function ExportDialog({
  participations,
  onClose,
}: {
  participations: Participation[];
  onClose: () => void;
}) {
  const csv = useMemo(() => buildExportCsv(participations), [participations]);
  const [copied, setCopied] = useState(false);
  const rowCount = csv.split('\n').length - 1;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(csv);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // no-op — user can still select & copy manually
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl space-y-3 rounded-lg border border-slate-800 bg-slate-950 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold">Export starters & subs</h3>
            <p className="text-xs text-slate-400">
              {rowCount} rows · CSV · flex flag taken from each slot's setting
            </p>
          </div>
          <button
            onClick={onCopy}
            className="inline-flex items-center gap-1 rounded border border-indigo-700 px-2 py-1 text-sm text-indigo-200 hover:bg-indigo-900/30"
          >
            <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <textarea
          readOnly
          value={csv}
          onFocus={(e) => e.currentTarget.select()}
          rows={16}
          className="w-full rounded border border-slate-800 bg-slate-950 p-2 font-mono text-xs text-slate-200"
        />
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Change history
// ------------------------------------------------------------

const SOURCE_LABEL: Record<string, string> = {
  autofill_even: 'Autofill · Even both slots',
  autofill_strong_a_weak_b: 'Autofill · Strong A, Weak B',
  autofill_weak_a_strong_b: 'Autofill · Weak A, Strong B',
  autofill_strong_a: 'Autofill · Strong-Slot A',
  autofill_strong_b: 'Autofill · Strong-Slot B',
  autofill_weak_a: 'Autofill · Weak-Slot A',
  autofill_weak_b: 'Autofill · Weak-Slot B',
  clear_slot_a: 'Cleared Slot A',
  clear_slot_b: 'Cleared Slot B',
  copy_a_to_b: 'Copied Slot A applications → B',
  import_a: 'Imported signups · Slot A',
  import_b: 'Imported signups · Slot B',
  paste_apps_a: 'Pasted applications · Slot A',
  paste_apps_b: 'Pasted applications · Slot B',
};

function assignLabel(
  eventType: EventType,
  slot: SlotKey | null,
  role: AssignedRole | null,
): string {
  if (slot == null) return '—';
  const prefix = eventType === 'canyon_storm' ? 'CS' : 'DS';
  const roleStr = role === 'starter' ? 'Starter' : role === 'sub' ? 'Sub' : '?';
  return `${prefix} ${slot} ${roleStr}`;
}

function describeEntry(entry: EventChangeLogEntry, eventType: EventType): string {
  const from = entry.fromSlot;
  const to = entry.toSlot;
  if (from == null && to != null) {
    return `Added to ${assignLabel(eventType, to, entry.toRole)}`;
  }
  if (from != null && to == null) {
    return `Dropped from ${assignLabel(eventType, from, entry.fromRole)}`;
  }
  if (from != null && to != null) {
    return `Moved ${assignLabel(eventType, from, entry.fromRole)} → ${assignLabel(
      eventType,
      to,
      entry.toRole,
    )}`;
  }
  return 'No change';
}

type EntryGroup = {
  key: string;
  source: string;
  changedByName: string | null;
  changedAt: string;
  entries: EventChangeLogEntry[];
};

function groupEntries(entries: EventChangeLogEntry[]): EntryGroup[] {
  // entries are already sorted newest → oldest by the API.
  const groups: EntryGroup[] = [];
  const WINDOW_MS = 5000;
  for (const e of entries) {
    const last = groups[groups.length - 1];
    const t = new Date(e.changedAt).getTime();
    const canJoin =
      last &&
      last.source === e.source &&
      last.changedByName === e.changedByName &&
      Math.abs(new Date(last.changedAt).getTime() - t) <= WINDOW_MS;
    if (canJoin) {
      last!.entries.push(e);
    } else {
      groups.push({
        key: `${e.id}`,
        source: e.source,
        changedByName: e.changedByName,
        changedAt: e.changedAt,
        entries: [e],
      });
    }
  }
  return groups;
}

function formatWhen(iso: string): string {
  return iso.slice(0, 16).replace('T', ' ');
}

function ChangeLogSection({
  eventId,
  eventType,
}: {
  eventId: number;
  eventType: EventType;
}) {
  const q = useQuery({
    queryKey: ['event-changelog', eventId],
    queryFn: () => api.listEventChangeLog(eventId),
  });
  const [expanded, setExpanded] = useState(false);

  const groups = useMemo(() => groupEntries(q.data ?? []), [q.data]);
  const total = q.data?.length ?? 0;
  const shown = expanded ? groups : groups.slice(0, 20);

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-950/40 p-4">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">Change history</h2>
        {total > 0 && (
          <span className="text-xs text-slate-500">{total} changes</span>
        )}
      </header>
      {q.isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="text-sm text-slate-500">No manual changes recorded yet.</div>
      ) : (
        <>
          <ul className="space-y-2">
            {shown.map((g) => (
              <GroupItem key={g.key} group={g} eventType={eventType} />
            ))}
          </ul>
          {!expanded && groups.length > 20 && (
            <button
              onClick={() => setExpanded(true)}
              className="mt-3 text-xs text-slate-400 hover:text-slate-200"
            >
              Show all {groups.length} entries
            </button>
          )}
        </>
      )}
    </section>
  );
}

function GroupItem({
  group,
  eventType,
}: {
  group: EntryGroup;
  eventType: EventType;
}) {
  const isBatch =
    group.source !== 'manual' && group.entries.length > 0;
  const who = group.changedByName ?? 'system';
  if (isBatch) {
    const label = SOURCE_LABEL[group.source] ?? group.source;
    return (
      <li className="rounded border border-slate-800 bg-slate-900/40">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 px-3 py-1.5 text-xs">
          <span className="rounded bg-indigo-900/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo-200">
            {label}
          </span>
          <span className="text-slate-400">by {who}</span>
          <span className="ml-auto text-slate-500">
            {formatWhen(group.changedAt)} · {group.entries.length}{' '}
            {group.entries.length === 1 ? 'change' : 'changes'}
          </span>
        </div>
        <ul className="divide-y divide-slate-900">
          {group.entries.map((e) => (
            <EntryRow key={e.id} entry={e} eventType={eventType} showWho={false} />
          ))}
        </ul>
      </li>
    );
  }
  // single manual change
  const e = group.entries[0]!;
  return (
    <li>
      <EntryRow entry={e} eventType={eventType} showWho />
    </li>
  );
}

function EntryRow({
  entry,
  eventType,
  showWho,
}: {
  entry: EventChangeLogEntry;
  eventType: EventType;
  showWho: boolean;
}) {
  const name = entry.playerDisplayName?.trim() || entry.playerName;
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs">
      {showWho && (
        <span className="w-32 shrink-0 tabular-nums text-slate-500">
          {formatWhen(entry.changedAt)}
        </span>
      )}
      <span className="font-medium text-slate-200">{name}</span>
      <span className="text-slate-400">{describeEntry(entry, eventType)}</span>
      {showWho && (
        <span className="ml-auto text-[10px] uppercase text-slate-500">
          by {entry.changedByName ?? 'system'}
        </span>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Event video import jobs strip. Same UX as the VS one — persistent jobs
// stay visible until applied or deleted, so a user can kick off an upload
// and come back later.
// -----------------------------------------------------------------------------

function EventImportJobsStrip({
  eventId,
  jobs,
  onOpen,
}: {
  eventId: number;
  jobs: ImportVideoJobState[];
  onOpen: (jobId: string) => void;
}) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: (jobId: string) => api.deleteEventImportJob(eventId, jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-import-jobs', eventId] }),
  });

  const statusChip: Record<string, { label: string; cls: string }> = {
    queued: { label: 'queued', cls: 'bg-slate-800 text-slate-300 border-slate-700' },
    running: { label: 'running', cls: 'bg-indigo-950/60 text-indigo-200 border-indigo-800' },
    done: { label: 'ready', cls: 'bg-emerald-950/60 text-emerald-200 border-emerald-800' },
    failed: { label: 'failed', cls: 'bg-red-950/60 text-red-200 border-red-800' },
    discarded: { label: 'discarded', cls: 'bg-slate-800 text-slate-500 border-slate-700' },
  };

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
      <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
        Video imports
      </div>
      <ul className="divide-y divide-slate-800">
        {jobs.map((j) => {
          const chip = statusChip[j.status] ?? statusChip.queued;
          const stageLabel =
            j.status === 'running'
              ? j.stage === 'uploading'
                ? 'uploading'
                : j.stage === 'processing'
                  ? 'extracting'
                  : 'running'
              : chip.label;
          return (
            <li key={j.id} className="flex items-center gap-3 py-2 text-sm">
              <span
                className={
                  'inline-flex w-24 justify-center rounded border px-2 py-0.5 text-xs ' +
                  chip.cls
                }
              >
                {stageLabel}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-slate-200">
                  Slot {j.slot}
                  {j.error && (
                    <span className="ml-2 text-red-400 truncate">· {j.error}</span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {j.createdAt?.replace('T', ' ').slice(0, 16)} ·{' '}
                  {j.videoBytes ? `${Math.round((j.videoBytes / 1024 / 1024) * 10) / 10} MB` : ''}
                </div>
              </div>
              {(j.status === 'done') && (
                <button
                  onClick={() => onOpen(j.id)}
                  className="rounded border border-emerald-800 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-900/40"
                >
                  Open preview
                </button>
              )}
              {j.status === 'failed' && (
                <button
                  onClick={() => onOpen(j.id)}
                  className="rounded border border-red-800 px-2 py-1 text-xs text-red-200 hover:bg-red-900/40"
                >
                  Retry
                </button>
              )}
              {(j.status === 'queued' || j.status === 'running') && (
                <button
                  onClick={() => onOpen(j.id)}
                  className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                >
                  Watch
                </button>
              )}
              {j.status !== 'running' && (
                <button
                  onClick={() => {
                    if (confirm('Delete this import job? The stored video will be removed.')) {
                      del.mutate(j.id);
                    }
                  }}
                  className="rounded border border-red-900 px-2 py-1 text-xs text-red-300 hover:bg-red-900/40"
                  title="Delete job"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
